import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { DollarBillSvg } from "./HiringInstantPayDollarBill";

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  velocity: number;
  swing: number;
  delay: number;
  distance: number;
}

interface HiringInstantPayConfettiProps {
  isActive: boolean;
  onComplete: () => void;
}

const BILL_SIZE = 20;
const PIECE_COUNT = 30;
const ANIMATION_DURATION = 1800;

export function HiringInstantPayConfetti(props: HiringInstantPayConfettiProps) {
  const { isActive, onComplete } = props;
  const theme = useTheme();
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  useEffect(() => {
    if (isActive) {
      // Generate confetti pieces starting from bottom
      const newPieces: ConfettiPiece[] = [];
      for (let index = 0; index < PIECE_COUNT; index += 1) {
        newPieces.push({
          id: index,
          x: Math.random() * 100,
          y: 110,
          rotation: Math.random() * 360,
          velocity: 0.5 + Number(Math.random()),
          swing: -30 + Math.random() * 60,
          delay: Math.random() * 300,
          distance: 40 + Math.random() * 25,
        });
      }

      setPieces(newPieces);

      // Clear after animation completes
      const timer = setTimeout(() => {
        setPieces([]);
        onComplete();
      }, ANIMATION_DURATION);

      return () => {
        clearTimeout(timer);
      };
    }

    return undefined;
  }, [isActive, onComplete]);

  if (!isActive || pieces.length === 0) {
    return null;
  }

  const confettiElement = (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 99_999,
        overflow: "hidden",
      }}
    >
      {pieces.map((piece) => (
        <Box
          key={piece.id}
          sx={{
            position: "absolute",
            left: `${piece.x}%`,
            top: `${piece.y}%`,
            width: BILL_SIZE * 2,
            height: BILL_SIZE,
            animation: `confettiLaunch-${piece.id} ${ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1) forwards`,
            animationDelay: `${piece.delay}ms`,
            [`@keyframes confettiLaunch-${piece.id}`]: {
              "0%": {
                transform: `translateY(0) translateX(0) rotate(${piece.rotation}deg)`,
                opacity: 1,
              },
              "60%": {
                opacity: 1,
              },
              "100%": {
                transform: `translateY(-${piece.distance}vh) translateX(${piece.swing}px) rotate(${
                  piece.rotation + 540
                }deg)`,
                opacity: 0,
              },
            },
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${BILL_SIZE * 2} ${BILL_SIZE}`}
            style={{ display: "block" }}
          >
            <DollarBillSvg
              x={0}
              y={0}
              rotation={0}
              width={BILL_SIZE * 2}
              height={BILL_SIZE}
              fillColor={theme.palette.success.light}
              strokeColor={theme.palette.success.dark}
            />
          </svg>
        </Box>
      ))}
    </Box>
  );

  return createPortal(confettiElement, document.body);
}
